import { UnreachableError } from 'base/helpers';

/** Takes an iterator over characters.  Returns an iterator over lines */
export async function* mapLines(
  chunks: AsyncIterable<string>,
  { yieldPartialLines }: { yieldPartialLines?: boolean } = {},
) {
  let buffer = '';
  for await (const chunk of chunks) {
    const [addToBuffer, ...rest] = chunk.split('\n');
    buffer += addToBuffer;
    for (const nextLine of rest) {
      yield buffer.normalize('NFC');
      buffer = nextLine;
    }
    if (yieldPartialLines && buffer.length > 0) yield buffer;
  }
  if (buffer.length > 0) yield buffer.normalize('NFC');
}

/**
 * Takes a stream of text.  After each chunk it yield what has been parsed so far.
 *
 * I think a truck hit my brain this morning.  Why did I think it was a good idea to write a JSON parser from scratch?
 **/
export async function* mapPartialJson(
  chunks: AsyncIterable<string>,
  prefix = '',
): AsyncIterable<any> {
  let latestValue: any;
  const parser = parseJsonValue((value) => (latestValue = value));

  let state = parser.next();
  state = feedParser(parser, prefix, state);
  yield latestValue;
  if (state.done) return;

  for await (const chunk of stripPrefixIfPresent(chunks, prefix)) {
    state = feedParser(parser, chunk, state);
    yield latestValue;
    if (state.done) return;
  }
}

export async function* stripPrefixIfPresent(
  chunks: AsyncIterable<string>,
  prefix: string,
): AsyncIterable<string> {
  let buffer = '';
  let passedPrefix = false;
  for await (const chunk of chunks) {
    if (passedPrefix) {
      yield chunk;
      continue;
    }
    buffer += chunk;
    passedPrefix = buffer.length >= prefix.length || !prefix.startsWith(buffer);
    if (passedPrefix && buffer.startsWith(prefix)) {
      buffer = buffer.substr(prefix.length);
    }
    if (passedPrefix && buffer.length > 0) {
      yield buffer;
      buffer = '';
    }
  }
  if (buffer.length > 0) yield buffer;
}

function feedParser(
  parser: CharIterator,
  chars: string,
  state: CharIteratorResult,
): CharIteratorResult {
  let i = 0;
  try {
    while (i < chars.length) {
      const char = chars[i];
      if (state.done) {
        return state;
      } else if (state.value === 'take') {
        state = parser.next(char);
        i++;
      } else if (state.value === 'peek') {
        state = parser.next(char);
      } else {
        throw new UnreachableError(state.value);
      }
    }
    return state;
  } catch (e) {
    throw new Error(
      `Failed to parse json: ${e}\n` +
        `At: ${chars.substr(0, i + 1)}\n` +
        `Of: ${chars}\n` +
        `${(e as any)?.stack}`,
    );
  }
}

type CharIterator = Generator<'peek' | 'take', void, string>;
type CharIteratorResult = ReturnType<CharIterator['next']>;

/*
function createCharIterator(
  chunks: AsyncIterable<string>,
  buffer = '',
): CharIterator {
  const iterator = chunks[Symbol.asyncIterator]();
  let bufferIndex = 0;
  async function ensureBuffer() {
    if (buffer.length > bufferIndex) return;
    const it = await iterator.next();
    if (it.done) throw new Error(`unexpected end of input`);
    buffer = it.value;
    bufferIndex = 0;
    await ensureBuffer();
  }
  return {
    peek: async () => {
      await ensureBuffer();
      return buffer[bufferIndex];
    },
    take: async () => {
      await ensureBuffer();
      const char = buffer[bufferIndex];
      bufferIndex++;
      return char;
    },
  };
}
*/

function* skipMarkdownOpening(): CharIterator {
  const char = yield 'peek';
  if (char === '`') {
    yield 'take';
    yield 'take';
    yield 'take';
    const j = yield 'take';
    const s = yield 'take';
    const o = yield 'take';
    const n = yield 'take';
    if (j !== 'j' || s !== 's' || o !== 'o' || n !== 'n') {
      throw new Error(`expected 'json' but got '${char}${j}${s}${o}${n}'`);
    }
  } else {
    return;
  }
}

function* skipWhitespace(): CharIterator {
  while (true) {
    const char = yield 'peek';
    if (char === ' ' || char === '\n' || char === '\r' || char === '\t') {
      yield 'take';
    } else {
      return;
    }
  }
}

const escaped: Record<string, string> = {
  '"': '"',
  '\\': '\\',
  '/': '/',
  b: '\b',
  f: '\f',
  n: '\n',
  r: '\r',
  t: '\t',
};

function* parseJsonValue(output: (v: any) => void): CharIterator {
  let hasMarkdownFormatting = false;
  yield* skipWhitespace();
  const char = yield 'peek';
  if (char === '`') {
    // Opening markdown formatting block.
    if (!hasMarkdownFormatting) {
      hasMarkdownFormatting = true;
      yield* skipMarkdownOpening();
      yield* parseJsonValue(output);
    } else {
      // Closing markdown formatting block. This should be the end of
      // the input.
      yield 'take';
      yield 'take';
      yield 'take';
    }
  } else if (char === '"') {
    yield* parseJsonString(output);
  } else if (char === '{') {
    const obj: any = {};
    output(obj);
    yield* parseJsonObject(obj);
  } else if (char === '[') {
    const arr: any[] = [];
    output(arr);
    yield* parseJsonArray(arr);
  } else if (char === 't') {
    yield 'take';
    const r = yield 'take';
    const u = yield 'take';
    const e = yield 'take';
    if (r !== 'r' || u !== 'u' || e !== 'e') {
      throw new Error(`expected 'true' but got '${char}${r}${u}${e}'`);
    }
    output(true);
  } else if (char === 'f') {
    yield 'take';
    const a = yield 'take';
    const l = yield 'take';
    const s = yield 'take';
    const e = yield 'take';
    if (a !== 'a' || l !== 'l' || s !== 's' || e !== 'e') {
      throw new Error(`expected 'false' but got '${char}${a}${l}${s}${e}'`);
    }
    output(false);
  } else if (char === 'n') {
    yield 'take';
    const u = yield 'take';
    const l = yield 'take';
    const l2 = yield 'take';
    if (u !== 'u' || l !== 'l' || l2 !== 'l') {
      throw new Error(`expected 'null' but got '${char}${u}${l}${l2}'`);
    }
    output(null);
  } else if (char === '-' || (char >= '0' && char <= '9')) {
    yield* parseJsonNumber(output);
  } else {
    throw new Error(`unexpected character '${char}'`);
  }
}

function* parseJsonNumber(output: (v: number) => void): CharIterator {
  let buffer = '';
  while (true) {
    const char = yield 'peek';
    const code = char.charCodeAt(0);
    if (
      char === '-' ||
      char === 'e' ||
      char === 'E' ||
      char === '.' ||
      (code >= 48 && code <= 57)
    ) {
      buffer += yield 'take';
    } else {
      break;
    }
  }
  output(JSON.parse(buffer));
}

function* parseJsonString(output: (v: string) => void): CharIterator {
  const opening = yield 'take';
  if (opening !== '"') throw new Error(`expected '"' but got '${opening}'`);
  let buffer = '';
  output(buffer);
  while (true) {
    const char = yield 'take';
    if (char === '"') {
      return;
    }
    if (char === '\\') {
      const e1 = yield 'take';
      if (escaped[e1] !== undefined) {
        buffer += escaped[e1];
        output(buffer);
      } else if (e1 === 'u') {
        const e2 = yield 'take';
        const e3 = yield 'take';
        const e4 = yield 'take';
        const e5 = yield 'take';
        const code = parseInt(e2 + e3 + e4 + e5, 16);
        buffer += String.fromCharCode(code);
        output(buffer.normalize('NFC'));
      } else {
        throw new Error(`invalid escape sequence '\\${e1}'`);
      }
    } else {
      buffer += char;
      output(buffer);
    }
  }
}

function* parseJsonObject(output: any): CharIterator {
  const opening = yield 'take';
  if (opening !== '{') throw new Error(`expected '{' but got '${opening}'`);
  while (true) {
    yield* skipWhitespace();
    const char = yield 'peek';
    if (char === '}') {
      yield 'take';
      return;
    }
    if (char !== '"') {
      throw new Error(`expected start of property name but got '${char}'`);
    }
    let name = '';
    yield* parseJsonString((v) => (name = v));

    yield* skipWhitespace();
    const colon = yield 'take';
    if (colon !== ':') throw new Error(`expected ':' but got '${colon}'`);

    yield* skipWhitespace();
    yield* parseJsonValue((v) => (output[name] = v));
    yield* skipWhitespace();
    if ((yield 'peek') === ',') {
      yield 'take';
    }
  }
}

function* parseJsonArray(output: any[]): CharIterator {
  const opening = yield 'take';
  if (opening !== '[') throw new Error(`expected '[' but got '${opening}'`);
  while (true) {
    yield* skipWhitespace();
    if ((yield 'peek') === ']') {
      yield 'take';
      return;
    }
    let index = output.length;
    yield* parseJsonValue((v) => (output[index] = v));
    yield* skipWhitespace();
    if ((yield 'peek') === ',') {
      yield 'take';
    }
  }
}
